import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, Stack, Avatar, IconButton, Menu, MenuItem as MenuItemMUI, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, TextField, InputAdornment, Switch, FormControlLabel, Tooltip, Autocomplete, Chip, Button, ButtonGroup } from '@mui/material';
import { MoreVert, Settings } from '@mui/icons-material';
import userService, { AppUser, createFieldWorker, deleteFieldWorker } from '../../services/userService';
import roleService, { RoleDto } from '../../services/roleService';
import projectService, { Project, Offer } from '../../services/projectService';

function generateWorkerPassword(): string {
  const L = 'abcdefghijkmnopqrstuvwxyz';
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const D = '23456789';
  const S = '!@#$%&*';
  const all = L + U + D + S;
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  const pick = (set: string, i: number) => set[buf[i] % set.length];
  let out = pick(L, 0) + pick(U, 1) + pick(D, 2) + pick(S, 3);
  for (let i = 4; i < 16; i++) {
    out += all[buf[i] % all.length];
  }
  return out.split('').sort(() => Math.random() - 0.5).join('');
}

const UsersAssignRoles: React.FC = () => {
  const [users, setUsers] = React.useState<AppUser[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [roles, setRoles] = React.useState<RoleDto[]>([]);
  const [query, setQuery] = React.useState('');
  const [onlyActive, setOnlyActive] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await userService.listUsers();
      setUsers(list);
      // Load all available roles (from roles table + distinct users.role)
      try {
        const rs = await roleService.listRoles();
        setRoles(rs);
      } catch (e) {
        setRoles([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Menu akcji + dialog uprawnień
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = React.useState<string | null>(null);
  const [permOpen, setPermOpen] = React.useState(false);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportPeriod, setReportPeriod] = React.useState<'day'|'week'|'month'|'halfyear'|'year'>('day');
  const [reportData, setReportData] = React.useState<any[]>([]);
  const [showTechnical, setShowTechnical] = React.useState(false);
  const [tab, setTab] = React.useState(0); // 0=projekty, 1=oferty
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [offers, setOffers] = React.useState<Offer[]>([]);
  const [projectSearch, setProjectSearch] = React.useState('');
  const [offerSearch, setOfferSearch] = React.useState('');
  const [projectSet, setProjectSet] = React.useState<Set<string>>(new Set());
  const [offerSet, setOfferSet] = React.useState<Set<string>>(new Set());

  const [fieldWorkerOpen, setFieldWorkerOpen] = React.useState(false);
  const [fwSubmitting, setFwSubmitting] = React.useState(false);
  const [fwLogin, setFwLogin] = React.useState('');
  const [fwFirst, setFwFirst] = React.useState('');
  const [fwLast, setFwLast] = React.useState('');
  const [fwPhone, setFwPhone] = React.useState('');
  const [fwPosition, setFwPosition] = React.useState('');
  const [fwPassword, setFwPassword] = React.useState('');
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, userId: string) => { setMenuAnchor(e.currentTarget); setMenuUserId(userId); };
  const closeMenu = () => { setMenuAnchor(null); };
  const openPermDialog = async () => {
    if (!menuUserId) return;
    try {
      const u = users.find(x => x.user_id === menuUserId);
      // Inicjalna selekcja: jeśli brak CSV => pełny dostęp (wszystko włączone)
      const allProjects = await projectService.getAllProjects();
      const allOffers = await projectService.getAllOffersMinimal();
      setProjects(allProjects);
      setOffers(allOffers);
      const projSet = new Set<string>();
      const offSet = new Set<string>();
      if (u?.project_list) u.project_list.split(',').map(s => s.trim()).filter(Boolean).forEach(id => projSet.add(id));
      if (u?.offers_list) u.offers_list.split(',').map(s => s.trim()).filter(Boolean).forEach(id => offSet.add(id));
      // Jeżeli brak listy => włącz wszystko (pełny dostęp)
      if (!u?.project_list) allProjects.forEach(p => projSet.add(p.project_id));
      if (!u?.offers_list) allOffers.forEach(o => o.offer_id && offSet.add(o.offer_id));
      setProjectSet(projSet);
      setOfferSet(offSet);
    } finally {
      setPermOpen(true);
      closeMenu();
    }
  };

  const openReportDialog = async () => {
    if (!menuUserId) return;
    setReportOpen(true);
    await loadReport('day');
    closeMenu();
  };

  const loadReport = async (period: 'day'|'week'|'month'|'halfyear'|'year') => {
    if (!menuUserId) return;
    setReportPeriod(period);
    try {
      if (period === 'day') {
        const rows = await userService.getUserActivitySummary(menuUserId, 'day');
        setReportData(rows);
      } else {
        const rows = await userService.getUserActivitySummary(menuUserId, period);
        setReportData(rows);
      }
    } catch (e) {
      setReportData([]);
    }
  };
  const saveImmediate = async (nextProjects: Set<string>, nextOffers: Set<string>) => {
    if (!menuUserId) return;
    const project_list = Array.from(nextProjects).join(',');
    const offers_list = Array.from(nextOffers).join(',');
    try {
      await userService.updateUserPermissions(menuUserId, {}, project_list, offers_list);
      // Zaktualizuj lokalny stan użytkowników, aby ponowne otwarcie dialogu używało świeżych CSV
      setUsers(prev => prev.map(u => u.user_id === menuUserId ? { ...u, project_list, offers_list } as AppUser : u));
    } catch (e) {
      // pomijamy błąd – kolejny toggle spróbuje ponownie
    }
  };

  const handleChangeRole = async (userId: string, role: string) => {
    if (!role || !role.trim()) return; // ignoruj puste
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: role.trim() } as AppUser : u));
    try {
      await userService.updateUserRole(userId, role.trim());
      // Dociągnij świeże dane z backendu (np. gdy DB normalizuje wielkość liter)
      await load();
    } catch (e) {
      // Nie cofaj wizualnie – pokaż błąd w konsoli; użytkownik może spróbować ponownie
      console.error('updateUserRole failed', e);
    }
  };

  const roleNames = React.useMemo(() => {
    const set = new Set<string>();
    roles.forEach(r => r?.name && set.add(r.name));
    // Ensure currently assigned roles remain selectable even if not in roles list
    users.forEach(u => u.role && set.add(u.role));
    if (set.size === 0) {
      ['admin', 'manager', 'user', 'field_worker'].forEach(r => set.add(r));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [roles, users]);

  // Tryb zmiany szerokości kolumn (wzorzec jak w module projekty)
  const headerCellSx: any = React.useMemo(() => ({
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
    position: 'relative'
  }), []);
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    user: 360,
    position: 160,
    phone: 200,
    role: 220,
    status: 120,
    lastLogin: 170,
    created: 120,
    actions: 72,
  }), []);
  const STORAGE_KEY = React.useMemo(() => 'usersTableColWidths', []);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(48, dragCol.startW + dx);
      setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none' }}>
      <span>{node}</span>
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => setDragCol({ key: keyId, startX: e.clientX, startW: getWidthNum(keyId) })}
          sx={{ position: 'absolute', right: 0, top: 0, width: '6px', height: '100%', cursor: 'col-resize', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}
        />
      ) : null}
    </TableCell>
  );

  // Helpers dla raportu – przyjaźniejsze dla HR
  const humanizeAction = (action: string, method?: string): string => {
    const map: Record<string, string> = {
      create_package: 'Utworzył pakiet',
      update_package: 'Zaktualizował pakiet',
      delete_package: 'Usunął pakiet',
      create_inquiry: 'Utworzył zapytanie ofertowe',
      update_inquiry: 'Zaktualizował zapytanie ofertowe',
      delete_inquiry: 'Usunął zapytanie ofertowe',
      create_offer: 'Utworzył ofertę',
      update_offer: 'Zaktualizował ofertę',
      delete_offer: 'Usunął ofertę',
      create_order: 'Utworzył zamówienie',
      update_order: 'Zaktualizował zamówienie',
      delete_order: 'Usunął zamówienie',
      create_contract_draft: 'Utworzył projekt umowy',
      update_contract_draft: 'Zaktualizował projekt umowy',
      delete_contract_draft: 'Usunął projekt umowy',
      create_kzm: 'Utworzył kartę KZM',
      create_transfer_invoice: 'Utworzył fakturę (przelewową/proforma)',
    };
    if (map[action]) return map[action];
    if (action?.includes('presence') || action?.includes('heartbeat')) return 'Aktywność w aplikacji';
    return method ? `Operacja ${method}` : 'Operacja';
  };

  const extractHttpMethod = (action: string): string | null => {
    const m = action?.match(/^(GET|POST|PUT|PATCH|DELETE)\b/i);
    return m ? m[0].toUpperCase() : null;
  };

  const humanizeDetails = (row: any): string => {
    const typeMap: Record<string, string> = {
      Package: 'Pakiet',
      Inquiry: 'Zapytanie',
      OfferStandalone: 'Oferta',
      Order: 'Zamówienie',
      ContractDraft: 'Projekt umowy',
      KZM: 'KZM',
      TransferInvoice: 'Faktura',
    };
    const parts: string[] = [];
    if (row?.entity_type && row?.entity_id) {
      const t = typeMap[row.entity_type] || row.entity_type;
      parts.push(`${t} #${row.entity_id}`);
    }
    if (row?.project_id) parts.push(`Projekt ${row.project_id}`);
    return parts.join(' • ');
  };

  const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString('pl-PL') : '');
  const formatStatus = (code?: number) => {
    if (typeof code !== 'number') return '';
    if (code >= 200 && code < 300) return 'Sukces';
    if (code >= 400 && code < 500) return `Błąd ${code}`;
    if (code >= 500) return `Błąd serwera (${code})`;
    return String(code);
  };

  const filteredDayRows = React.useMemo(() => {
    if (reportPeriod !== 'day') return [] as any[];
    const rows = Array.isArray(reportData) ? reportData : [];
    return rows.filter((r: any) => (showTechnical ? true : !(String(r.action||'').includes('presence') || String(r.route||'').includes('/api/presence/heartbeat'))));
  }, [reportData, reportPeriod, showTechnical]);

  const filteredUsers = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter(u => {
      if (onlyActive && u.is_active === false) return false;
      if (!q) return true;
      const name = (u.display_name || `${u.first_name || ''} ${u.last_name || ''}`).toLowerCase();
      const loginQ = (u.mobile_login || '').toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q) || loginQ.includes(q) || (u.role || '').toLowerCase().includes(q);
    });
  }, [users, query, onlyActive]);

  const openFieldWorkerDialog = () => {
    setFwLogin('');
    setFwFirst('');
    setFwLast('');
    setFwPhone('');
    setFwPosition('');
    setFwPassword(generateWorkerPassword());
    setFieldWorkerOpen(true);
  };

  const menuSelectedUser = React.useMemo(
    () => (menuUserId ? users.find((x) => x.user_id === menuUserId) : undefined),
    [menuUserId, users]
  );

  const handleDeleteFieldWorker = async () => {
    const uid = menuUserId;
    const u = menuSelectedUser;
    closeMenu();
    if (!uid || !u || u.role !== 'field_worker') return;
    const label = u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.mobile_login || uid;
    const ok = window.confirm(
      `Usunąć konto mobilne pracownika „${label}” (login: ${u.mobile_login || '—'})?\n\nOperacja jest nieodwracalna — użytkownik nie zaloguje się ponownie w aplikacji „Unitresco Praca”.`
    );
    if (!ok) return;
    try {
      await deleteFieldWorker(uid);
      await load();
    } catch (e: any) {
      window.alert(e?.response?.data?.message || e?.message || 'Nie udało się usunąć konta');
    }
  };

  const submitFieldWorker = async () => {
    const login = fwLogin.trim().toLowerCase();
    if (!/^[a-z0-9._-]{3,40}$/.test(login)) {
      window.alert('Login: 3–40 znaków — małe litery, cyfry, . _ -');
      return;
    }
    if (!fwFirst.trim() || !fwLast.trim()) {
      window.alert('Podaj imię i nazwisko.');
      return;
    }
    setFwSubmitting(true);
    try {
      const data = await createFieldWorker({
        mobile_login: login,
        first_name: fwFirst.trim(),
        last_name: fwLast.trim(),
        telefon: fwPhone.trim() || undefined,
        position: fwPosition.trim() || undefined,
        temporary_password: fwPassword.trim() || undefined,
      });
      setFieldWorkerOpen(false);
      const msg = `Konto utworzone.\n\nLogin mobilny: ${data.mobile_login}\nHasło tymczasowe: ${data.temporary_password}\n\nPrzekaż te dane pracownikowi (hasło nie będzie ponownie wyświetlone).`;
      try {
        await navigator.clipboard.writeText(`Login: ${data.mobile_login}\nHasło: ${data.temporary_password}`);
      } catch { /* ignore */ }
      window.alert(msg);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Błąd tworzenia konta';
      window.alert(msg);
    } finally {
      setFwSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Użytkownicy</Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Szukaj: imię, login, e‑mail, rola" value={query} onChange={(e) => setQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">🔎</InputAdornment> }} sx={{ maxWidth: 360 }} />
        <FormControlLabel control={<Switch checked={onlyActive} onChange={(_, v) => setOnlyActive(v)} />} label="Aktywni" />
        <Button variant="contained" color="primary" onClick={openFieldWorkerDialog} sx={{ ml: 'auto' }}>
          Dodaj pracownika fizycznego
        </Button>
      </Stack>
      <Box sx={{ position: 'relative' }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', position: 'relative' }}>
        {/* Ikona nad kolumną po prawej, poza komórkami headera */}
        <Box sx={{ position: 'absolute', top: -10, right: 2, zIndex: 10, pointerEvents: 'auto' }}>
          <Tooltip title={colResizeMode ? 'Zablokuj zmianę szerokości kolumn' : 'Odblokuj zmianę szerokości kolumn'}>
            <IconButton size="small" onClick={() => setColResizeMode(v => !v)} color={colResizeMode ? 'primary' : 'default'}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {renderResizableTh('user', 'Użytkownik')}
              {renderResizableTh('position', 'Stanowisko')}
              {renderResizableTh('phone', 'Telefon')}
              {renderResizableTh('role', 'Rola')}
              {renderResizableTh('status', 'Status')}
              {renderResizableTh('lastLogin', 'Ostatnie logowanie')}
              {renderResizableTh('created', 'Utworzono')}
              {renderResizableTh('actions', 'Akcje', 'right')}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(u => (
              <TableRow key={u.user_id} hover>
                <TableCell sx={{ width: getColW('user') }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={u.avatar_url || undefined} sx={{ width: 25, height: 25 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Tooltip title={u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} placement="top-start">
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 280 }}>
                          {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                        </span>
                      </Tooltip>
                      {u.role === 'field_worker' ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280 }}>
                            Login mobilny: <strong>{u.mobile_login || '—'}</strong>
                          </Typography>
                          {u.password_must_change ? (
                            <Chip size="small" label="Pierwsza zmiana hasła" color="warning" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                          ) : null}
                        </Stack>
                      ) : (
                        <Tooltip title={u.email} placement="top-start">
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.email}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ width: getColW('position') }}>
                  <Tooltip title={u.position || ''} placement="top-start">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 140 }}>{u.position || '—'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: getColW('phone') }}>
                  <span style={{ whiteSpace: 'nowrap' }}>{u.telefon || '—'}</span>
                </TableCell>
                <TableCell sx={{ width: getColW('role') }}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={roleNames}
                    value={u.role || ''}
                    onChange={(_, newValue) => handleChangeRole(u.user_id, String(newValue || 'user'))}
                    renderInput={(params) => <TextField {...params} placeholder="Rola" />}
                  />
                </TableCell>
                <TableCell sx={{ width: getColW('status') }}>
                  {u.is_active ? <Chip size="small" label="aktywne" color="success" variant="outlined" /> : <Chip size="small" label="zablokowane" color="default" variant="outlined" />}
                </TableCell>
                <TableCell sx={{ width: getColW('lastLogin') }}>{u.updated_at ? new Date(u.updated_at).toLocaleString().slice(0, 16) : '—'}</TableCell>
                <TableCell sx={{ width: getColW('created') }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="right" sx={{ width: getColW('actions') }}>
                  <IconButton size="small" onClick={(e) => openMenu(e, u.user_id)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filteredUsers.length === 0 && (
              <TableRow><TableCell colSpan={8}>Brak użytkowników</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={fieldWorkerOpen} onClose={() => !fwSubmitting && setFieldWorkerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nowy pracownik fizyczny (aplikacja mobilna)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bez adresu e-mail. Pracownik loguje się <strong>loginem mobilnym</strong> i hasłem. Hasło w bazie jest przechowywane wyłącznie jako hash (bcrypt).
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Login mobilny"
              size="small"
              fullWidth
              required
              value={fwLogin}
              onChange={(e) => setFwLogin(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
              helperText="3–40 znaków: małe litery, cyfry, kropka, podkreślenie, myślnik"
            />
            <Stack direction="row" spacing={1}>
              <TextField label="Imię" size="small" fullWidth required value={fwFirst} onChange={(e) => setFwFirst(e.target.value)} />
              <TextField label="Nazwisko" size="small" fullWidth required value={fwLast} onChange={(e) => setFwLast(e.target.value)} />
            </Stack>
            <TextField label="Telefon (opcjonalnie)" size="small" fullWidth value={fwPhone} onChange={(e) => setFwPhone(e.target.value)} />
            <TextField label="Stanowisko (opcjonalnie)" size="small" fullWidth value={fwPosition} onChange={(e) => setFwPosition(e.target.value)} />
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Hasło tymczasowe"
                size="small"
                fullWidth
                value={fwPassword}
                onChange={(e) => setFwPassword(e.target.value)}
                helperText="Min. 12 znaków, mała i wielka litera, cyfra, znak specjalny. Pracownik zmieni je przy pierwszym logowaniu."
              />
              <Button variant="outlined" onClick={() => setFwPassword(generateWorkerPassword())} sx={{ mt: 0.5, flexShrink: 0 }}>
                Wygeneruj
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldWorkerOpen(false)} disabled={fwSubmitting}>Anuluj</Button>
          <Button variant="contained" onClick={() => void submitFieldWorker()} disabled={fwSubmitting}>
            {fwSubmitting ? 'Tworzenie…' : 'Utwórz konto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu akcji */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItemMUI onClick={openPermDialog}>Modyfikuj uprawnienia</MenuItemMUI>
        <MenuItemMUI onClick={openReportDialog}>Raport aktywności</MenuItemMUI>
        {menuSelectedUser?.role === 'field_worker' ? (
          <MenuItemMUI onClick={() => void handleDeleteFieldWorker()} sx={{ color: 'error.main' }}>
            Usuń konto mobilne (pracownik fizyczny)
          </MenuItemMUI>
        ) : null}
      </Menu>

      {/* Dialog widoczności */}
      <Dialog open={permOpen} onClose={() => setPermOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Widoczność</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Projekty" />
            <Tab label="Oferty" />
          </Tabs>
          {tab === 0 && (
            <Box>
              <TextField
                fullWidth
                placeholder="Szukaj projekt..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">🔎</InputAdornment> }}
                sx={{ mb: 2 }}
              />
              <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Nazwa</TableCell>
                      <TableCell>Widoczne</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.filter(p => !projectSearch || p.project_id.includes(projectSearch) || p.project_name?.toLowerCase().includes(projectSearch.toLowerCase())).map(p => (
                      <TableRow key={p.project_id} hover>
                        <TableCell width={140}>{p.project_id}</TableCell>
                        <TableCell>{p.project_name}</TableCell>
                        <TableCell width={110}>
                          <FormControlLabel control={<Switch checked={projectSet.has(p.project_id)} onChange={(e) => {
                            setProjectSet(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(p.project_id); else next.delete(p.project_id);
                              saveImmediate(next, offerSet);
                              return next;
                            });
                          }} />} label="" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <TextField
                fullWidth
                placeholder="Szukaj ofertę..."
                value={offerSearch}
                onChange={(e) => setOfferSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">🔎</InputAdornment> }}
                sx={{ mb: 2 }}
              />
              <TableContainer component={Paper} sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Nazwa</TableCell>
                      <TableCell>Widoczne</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offers.filter(o => !offerSearch || (o.offer_id || '').includes(offerSearch) || (o.order_name || '').toLowerCase().includes(offerSearch.toLowerCase())).map(o => (
                      <TableRow key={o.offer_id} hover>
                        <TableCell width={160}>{o.offer_id}</TableCell>
                        <TableCell>
                          <Tooltip title={o.order_name || ''} placement="top-start">
                            <span>{(o.order_name || '').slice(0, 30)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell width={110}>
                          <FormControlLabel control={<Switch checked={!!o.offer_id && offerSet.has(o.offer_id)} onChange={(e) => {
                            if (!o.offer_id) return;
                            setOfferSet(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(o.offer_id!); else next.delete(o.offer_id!);
                              saveImmediate(projectSet, next);
                              return next;
                            });
                          }} />} label="" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog raportów */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Raport aktywności</DialogTitle>
        <DialogContent>
          <ButtonGroup size="small" sx={{ mb: 2 }}>
            <Button variant={reportPeriod === 'day' ? 'contained' : 'outlined'} onClick={() => loadReport('day')}>Dzień</Button>
            <Button variant={reportPeriod === 'week' ? 'contained' : 'outlined'} onClick={() => loadReport('week')}>Tydzień</Button>
            <Button variant={reportPeriod === 'month' ? 'contained' : 'outlined'} onClick={() => loadReport('month')}>Miesiąc</Button>
            <Button variant={reportPeriod === 'halfyear' ? 'contained' : 'outlined'} onClick={() => loadReport('halfyear')}>Półrocze</Button>
            <Button variant={reportPeriod === 'year' ? 'contained' : 'outlined'} onClick={() => loadReport('year')}>Rok</Button>
          </ButtonGroup>
          {reportPeriod === 'day' ? (
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={180}>Czas</TableCell>
                    <TableCell>Czynność</TableCell>
                    <TableCell>Szczegóły</TableCell>
                    <TableCell width={140}>Wynik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDayRows.map((r: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{formatDate(r.created_at)}</TableCell>
                      <TableCell>{humanizeAction(r.action, extractHttpMethod(r.action) || r.method)}</TableCell>
                      <TableCell>{humanizeDetails(r)}</TableCell>
                      <TableCell>{formatStatus(r.status_code)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredDayRows.length === 0 && (
                    <TableRow><TableCell colSpan={4}>Brak danych</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Okres</TableCell>
                    <TableCell>Liczba akcji</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((r: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{r.bucket}</TableCell>
                      <TableCell>{r.cnt}</TableCell>
                    </TableRow>
                  ))}
                  {reportData.length === 0 && (
                    <TableRow><TableCell colSpan={2}>Brak danych</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        {reportPeriod === 'day' && (
          <Box sx={{ px: 3, pb: 2 }}>
            <FormControlLabel control={<Switch checked={showTechnical} onChange={(_, v) => setShowTechnical(v)} />} label="Pokaż zdarzenia techniczne (heartbeat)" />
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default UsersAssignRoles;


